/**
 * Direction A — quiet conversation-channel delivery stamps under staff bubbles.
 * Labels are channel-generic; v1 adapter happens to be email.
 */

import type { ChannelDeliveryReason, ChannelDeliveryStatus } from '@/types/messaging';

export type { ChannelDeliveryReason, ChannelDeliveryStatus };

export function isChannelDeliveryReason(
  value: string | null | undefined,
): value is ChannelDeliveryReason {
  return (
    value === 'mailbox_disconnected' ||
    value === 'mailbox_needs_reconnect' ||
    value === 'mailbox_not_recorded' ||
    value === 'send_not_permitted' ||
    value === 'company_mismatch'
  );
}

export function isChannelDeliveryStatus(
  value: string | null | undefined,
): value is ChannelDeliveryStatus {
  return (
    value === 'pending' ||
    value === 'sending' ||
    value === 'sent' ||
    value === 'retrying' ||
    value === 'failed' ||
    value === 'skipped'
  );
}

/** True while the channel leg may still change — UI should refresh quietly. */
export function isInFlightChannelDelivery(
  status: string | null | undefined,
): boolean {
  return status === 'pending' || status === 'sending' || status === 'retrying';
}

/** The `consultantMessages.page.stamp` catalog key holding the stamp's words for a delivery status. */
export type ChannelDeliveryStampKey =
  | 'sending'
  | 'retrying'
  | 'sent'
  | 'failed'
  | 'skipped'
  | `reason_${ChannelDeliveryReason}`;

export function channelDeliveryStampKey(
  status: ChannelDeliveryStatus,
  reason?: ChannelDeliveryReason | null,
): ChannelDeliveryStampKey {
  // A reason only explains a leg that stopped; it never overrides an in-flight or sent stamp.
  if (reason && (status === 'skipped' || status === 'failed')) {
    return `reason_${reason}`;
  }
  switch (status) {
    case 'pending':
    case 'sending':
      return 'sending';
    case 'retrying':
      return 'retrying';
    case 'sent':
      return 'sent';
    case 'failed':
      return 'failed';
    case 'skipped':
      return 'skipped';
  }
}

/** Tone for the stamp row — subtle; amber only for in-progress retry / failure emphasis. */
export function channelDeliveryStampTone(
  status: ChannelDeliveryStatus,
): 'muted' | 'attention' {
  return status === 'retrying' || status === 'failed' ? 'attention' : 'muted';
}
